/**
 * interactionDomain — interprets `io.doclm.interaction.*`: the agent pausing for
 * the user.
 *
 * The wire carries values, not a form.
 */

import { asRecord } from 'src/content/ChatV2/lib/guards';
import type {
  DomainInterpreter,
  NormalizedEvent,
  SettledStepEntry
} from 'src/content/ChatV2/steps/model/types';
import {
  readFieldValue,
  toolDisplayName,
  toolTemplateFields
} from 'src/content/ChatV2/steps/toolTemplates';
import { EVENT_DOMAIN } from 'src/content/ChatV2/steps/wire/eventType';
import {
  type InterruptField,
  type InterruptStepDetail,
  type Step,
  STEP_DETAIL_KIND,
  STEP_KIND,
  STEP_STATUS,
  type StepInteraction
} from 'src/content/ChatV2/types/steps';
import { INTERRUPT_FIELD_KIND } from 'src/content/ChatV2/types/steps';
import { ACTION_INTERRUPT_TYPE } from 'src/types/enum';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

const CONFIRM_HEADING = T.confirmActionHeading;
const AUTH_HEADING = T.connectAccountHeading;
const AUTH_DESCRIPTION = T.connectAccountDescription;
const ARRAY_HINT = T.commaSeparated;

function interruptTypeOf(value: unknown): ACTION_INTERRUPT_TYPE {
  return value === ACTION_INTERRUPT_TYPE.USER_AUTH
    ? ACTION_INTERRUPT_TYPE.USER_AUTH
    : ACTION_INTERRUPT_TYPE.USER_CONFIRMATION;
}

function readInteraction(source: Record<string, unknown>): StepInteraction {
  return {
    type: interruptTypeOf(source.type),
    toolName:
      typeof source.tool_name === 'string' ? source.tool_name : undefined,
    args: asRecord(source.args),
    toolInfo: asRecord(source.tool_info),
    state: source.interrupted_state ?? source.state
  };
}

function buildFields(interaction: StepInteraction): InterruptField[] {
  return toolTemplateFields(interaction.toolName, interaction.args).map(
    (field) => ({
      id: field.key,
      label: field.label,
      fieldKind: field.multiline
        ? INTERRUPT_FIELD_KIND.TEXTAREA
        : INTERRUPT_FIELD_KIND.TEXT,
      value: readFieldValue(interaction.args, field.key),
      hint: field.isArray ? ARRAY_HINT : undefined
    })
  );
}

export const interactionDomain: DomainInterpreter = {
  domain: EVENT_DOMAIN.INTERACTION,

  identify(event) {
    const callId = event.data.tool_call_id;
    if (typeof callId === 'string' && callId !== '') {
      return `${EVENT_DOMAIN.TOOL}:${callId}`;
    }
    return event.turnId;
  },

  kind() {
    return STEP_KIND.INTERRUPT;
  },

  apply(step, event: NormalizedEvent) {
    const interaction = readInteraction(event.data);
    return {
      ...step,
      kind: STEP_KIND.INTERRUPT,
      label: undefined,
      interaction: {
        ...interaction,
        toolName: interaction.toolName ?? step.interaction?.toolName,
        args: interaction.args ?? step.interaction?.args,
        toolInfo: interaction.toolInfo ?? step.interaction?.toolInfo,
        state: interaction.state ?? step.interaction?.state
      }
    };
  },

  settle(entry: SettledStepEntry) {
    const type = entry.type;
    if (
      type !== ACTION_INTERRUPT_TYPE.USER_CONFIRMATION &&
      type !== ACTION_INTERRUPT_TYPE.USER_AUTH
    ) {
      return null;
    }
    return {
      status: STEP_STATUS.BLOCKED,
      kind: STEP_KIND.INTERRUPT,
      interaction: readInteraction(entry as Record<string, unknown>)
    } as Partial<Step>;
  },

  detail(step): InterruptStepDetail | undefined {
    const interaction = step.interaction;
    if (!interaction) {
      return undefined;
    }
    const isAuth = interaction.type === ACTION_INTERRUPT_TYPE.USER_AUTH;
    return {
      kind: STEP_DETAIL_KIND.INTERRUPT,
      interruptType: interaction.type,
      title:
        toolDisplayName(interaction.toolName) ??
        interaction.toolName ??
        T.wfAction,
      heading: isAuth ? AUTH_HEADING : CONFIRM_HEADING,
      description: isAuth ? AUTH_DESCRIPTION : undefined,
      fields: isAuth ? [] : buildFields(interaction)
    };
  }
};
